import React, { useState, useCallback, useMemo } from 'react';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import GavelIcon from '@mui/icons-material/Gavel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import Button from '@mui/material/Button';
import { toast } from '../../../common/toast';
import { NumericFormat } from 'react-number-format';
import { processSettlement, validateSettlementEligibility, clampDisplayedSettlement } from './settlementService';
import { usePersistedDataStore } from '../../store';
import { useUser } from '../../UserContext';
import { getConversionFactor } from '../../../utils/conversionUtils';
import { computeDirection, computeReductionPercent, applySettlementRounding } from './SettlementRounding.js';
import SvgIconButton from '../SvgIconButton';
import { convertAVToMV } from '../../../utils/conversionUtils';
import { calculateProprietyWorkup } from '../SummaryCaseTable/services/summaryRowCreationService.js';
import { getAVUtil, getIFMVUtil } from '../../../utils/getAVUtil';
import { createOfferContext } from '../../../utils/offerContextUtils';
import { getMostRecentOfferValue } from '../../../utils/mostRecentOfferUtils';
import logger from '../../../utils/logger';
import { isScarFiledForSettlement } from './settlementStageUtils.js';
import { isMasterPetitionCase } from '../SummaryCaseTable/utils/helpers.js';

// SettlementPopup component - now internal to SettlementButton
const SettlementPopup = ({ 
  handleClose, 
  onSettlementClick, 
  caseObject,
  currentOfferValue = null,
  isInitialValueInMV = true,
  ifmv = 0,
  isSubmitting = false
}) => {

  const [settlementRaw, setSettlementRaw] = useState(''); // raw digits only
  const [isAVMode, setIsAVMode] = useState(false);
  const [isWithdrawal, setIsWithdrawal] = useState(false);
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [customWithdrawalReason, setCustomWithdrawalReason] = useState('');
  const [withdrawalError, setWithdrawalError] = useState('');
  
  // Get rounding and downloadStips from persisted store
  const rounding = usePersistedDataStore((state) => state.settlementRounding);
  const setRounding = usePersistedDataStore((state) => state.setSettlementRounding);
  const downloadStips = usePersistedDataStore((state) => state.downloadStips);
  const setDownloadStips = usePersistedDataStore((state) => state.setDownloadStips);
  const villageRun = usePersistedDataStore((state) => state.negotiationObj?.villageRun ?? 0);
  const inputRef = React.useRef(null);
  const scarStageAvailable = useMemo(
    () => isScarFiledForSettlement(caseObject),
    [caseObject]
  );
  const art7StageAvailable = isMasterPetitionCase(caseObject);

  const [stage, setStage] = useState(() => (
    scarStageAvailable ? 'SCAR' : (art7StageAvailable ? 'Art7' : 'BAR')
  ));

  React.useEffect(() => {
    if (!scarStageAvailable && stage === 'SCAR') {
      setStage(art7StageAvailable ? 'Art7' : 'BAR');
    } else if (art7StageAvailable && stage === 'BAR') {
      setStage(scarStageAvailable ? 'SCAR' : 'Art7');
    }
  }, [scarStageAvailable, art7StageAvailable, stage]);

  // Withdrawal reason options
  const withdrawalReasons = [
    'Not owner occupied',
    'Income producing',
    'Duplicate filing',
    'Moratorium (Prior year reduction)',
    'Commercial',
    'Stip N/C',
    'Other'
  ];

  // Focus-to-end helper
  const focusToEnd = React.useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    
    // Find the actual input element within the MUI TextField
    const inputEl = el.querySelector('input');
    if (!inputEl) return;
    
    requestAnimationFrame(() => {
      inputEl.focus({ preventScroll: true });
      try { 
        // Use a high number to ensure cursor goes to the end
        inputEl.setSelectionRange(9999, 9999); 
      } catch {}
    });
  }, []);

  // Calculate conversion factor using the same logic as other components
  const conversionFactor = useMemo(() => {
    if (!caseObject) throw new Error('[SettlementButton] Missing caseObject.');
    return getConversionFactor(caseObject.EQ, caseObject.RAR);
  }, [caseObject]);

  // Handle withdrawal logic - set value to IFMV when withdrawal is selected
  React.useEffect(() => {
    if (isWithdrawal) {
      // Set value to IFMV if in MV mode, or convert IFMV to AV if in AV mode
      const ifmv = getIFMVUtil(caseObject);
      if (isAVMode) {
        // Convert IFMV to AV
        const avValue = Math.round(ifmv * conversionFactor);
        setSettlementRaw(String(avValue));
      } else {
        // Use IFMV directly in MV mode
        setSettlementRaw(String(Math.round(ifmv)));
      }
    }
  }, [isWithdrawal, isAVMode, caseObject, conversionFactor]);

  // Helper to safely parse numeric value
  const parseNumericValue = (value) => {
    if (!value || value === '') return null;
    const parsed = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    return isNaN(parsed) ? null : parsed;
  };

  // Initialize with current offer value if available (default only; allow user edits thereafter)
  React.useEffect(() => {
    if (currentOfferValue !== null && currentOfferValue !== undefined && currentOfferValue !== '') {
      const numeric = parseNumericValue(currentOfferValue);
      
      if (numeric !== null) {
        // If value is already in MV terms, use directly; otherwise convert AV to MV
        let marketValue;
        if (isInitialValueInMV) {
          // Value is already in MV terms, use as-is
          marketValue = Math.round(numeric);
        } else {
          // Value is in AV terms, convert to MV for display
          if (conversionFactor > 0) {
            marketValue = Math.round(numeric / conversionFactor);
          } else {
            marketValue = Math.round(numeric);
          }
        }
        setSettlementRaw(marketValue.toString());
      } else {
        setSettlementRaw('');
      }
    } else {
      setSettlementRaw('');
    }
    // Intentionally only respond to currentOfferValue changes so typing isn't overridden
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOfferValue, isInitialValueInMV, conversionFactor]);

  // Focus when the value is actually ready (first time only)
  const didInitFocus = React.useRef(false);
  React.useEffect(() => {
    if (didInitFocus.current) return;
    if (inputRef.current && (settlementRaw ?? '') !== '') {
      didInitFocus.current = true;
      // delay a tick so RNF has applied formatting & commas
      requestAnimationFrame(() => focusToEnd());
    }
  }, [settlementRaw, focusToEnd]);



  const handleModeToggle = useCallback(() => {
    const n = settlementRaw === '' ? null : parseInt(String(settlementRaw).replace(/,/g, ''), 10);
    const nextIsAV = !isAVMode;
    if (n !== null && Number.isFinite(n)) {
      const converted = isAVMode
        ? Math.round(n / conversionFactor / 10) * 10
        : Math.round(n * conversionFactor);
      setSettlementRaw(String(clampDisplayedSettlement(converted, nextIsAV, caseObject)));
    }
    setIsAVMode(nextIsAV);
    setTimeout(() => focusToEnd(), 0);
  }, [isAVMode, settlementRaw, conversionFactor, caseObject, focusToEnd]);

  const onSettlementAmountBlur = useCallback(() => {
    setSettlementRaw((prev) => {
      const trimmed = String(prev ?? '').replace(/,/g, '').trim();
      if (trimmed === '') return '';
      const parsed = parseInt(trimmed, 10);
      if (!Number.isFinite(parsed)) return prev;
      return String(clampDisplayedSettlement(parsed, isAVMode, caseObject));
    });
  }, [isAVMode, caseObject]);

  // Invoked when user clicks "Settle" in the popover (or presses Enter). Withdrawal and value validation run here before submit.
  const handleSettlement = useCallback(async () => {
    const trimmed = (settlementRaw ?? '').trim();
    if (!trimmed) {
      toast.error('Enter a settlement value', { autoClose: false });
      return;
    }

    // Validate withdrawal reason if withdrawal is selected
    if (isWithdrawal) {
      if (!withdrawalReason || withdrawalReason === '') {
        setWithdrawalError('Please select a withdrawal reason');
        return;
      }
      if (withdrawalReason === 'Other' && (!customWithdrawalReason || customWithdrawalReason.trim() === '')) {
        setWithdrawalError('Please enter a custom withdrawal reason');
        return;
      }
    }

    // Clear withdrawal error if validation passes
    setWithdrawalError('');
    
    try {
      const nRaw = parseInt(String(trimmed).replace(/,/g, ''), 10);
      if (!Number.isFinite(nRaw)) {
        toast.error('Enter a valid settlement value', { autoClose: false });
        return;
      }
      const numericValue = clampDisplayedSettlement(nRaw, isAVMode, caseObject);

      const currentAV = getAVUtil(caseObject);
      logger.log('settlement caseobject', caseObject);
      let finalValueAV = applySettlementRounding(numericValue, isAVMode, conversionFactor, rounding, currentAV);

      // Enforce min/max via validateSettlementEligibility; if value was adjusted, treat as error and update input
      const eligibility = validateSettlementEligibility(caseObject, { settlementValueAV: finalValueAV, settlementStage: stage });
      if (!eligibility.eligible && eligibility.adjustedValueAV != null) {
        const displayValue = isAVMode
          ? Math.round(eligibility.adjustedValueAV)
          : (conversionFactor > 0 ? Math.round(eligibility.adjustedValueAV / conversionFactor) : Math.round(eligibility.adjustedValueAV));
        setSettlementRaw(String(displayValue));
        return;
      }
      if (!eligibility.eligible) {
        toast.error(eligibility.reason || 'Settlement value is not valid; adjust and try again.', { autoClose: false });
        return;
      }

      // Create settlement data
      // Note: downloadStips is now read from persisted state, not passed here
      const settlementData = {
        caseObject,
        settlementValue: finalValueAV, // Store AV value in database
        originalValue: nRaw,
        isAVMode,
        rounding,
        conversionFactor,
        stage,
        isWithdrawal,
        withdrawalReason: isWithdrawal ? (withdrawalReason === 'Other' ? customWithdrawalReason : withdrawalReason) : ''
      };


      // Persist selected stage to localStorage
      try {
        const adv = JSON.parse(localStorage.getItem('advancedSettings') || '{}');
        adv.stage = stage;
        localStorage.setItem('advancedSettings', JSON.stringify(adv));
      } catch {}

      // Call the settlement callback
      if (onSettlementClick) {
        await onSettlementClick(settlementData);
      }

      // Close the popup
      handleClose();
    } catch (error) {
      logger.error('Error processing settlement:', error);
    }
  }, [settlementRaw, rounding, isAVMode, conversionFactor, caseObject, onSettlementClick, handleClose, isWithdrawal, withdrawalReason, customWithdrawalReason, stage]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSettlement();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  }, [handleSettlement, handleClose]);

  // Determine rounding direction and border color
  const getRoundingInfo = useCallback(() => {
    const n = settlementRaw === '' ? null : parseInt(settlementRaw, 10);
    if (!n) return { direction: null, borderColor: '#ccc' };
    const propertyAssessment = getAVUtil(caseObject);
    const avFinal = applySettlementRounding(n, isAVMode, conversionFactor, rounding, propertyAssessment);
    if (avFinal === Number(propertyAssessment)) return { direction: null, borderColor: '#ccc' };
    const direction = computeDirection(n, isAVMode, conversionFactor, avFinal);
    if (!direction) return { direction: null, borderColor: '#ccc' };
    return { direction, borderColor: direction === 'up' ? '#f44336' : '#4caf50' };
  }, [settlementRaw, rounding, isAVMode, conversionFactor, caseObject]);

  const roundingInfo = getRoundingInfo();

  return (
    <Box sx={{ p: 3, width: 300 }}>
      {/* Header with current values on the right */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Settle Case
        </Typography>
        
        {/* Current values display */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Current AV: {getAVUtil(caseObject) ? getAVUtil(caseObject).toLocaleString() : 'N/A'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            IFMV: ${ifmv ? ifmv.toLocaleString() : 'N/A'}
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
          Settlement Value
        </Typography>
        <NumericFormat
          value={settlementRaw}
          onValueChange={(vals) => setSettlementRaw(vals.value ?? '')}
          customInput={TextField}
          valueIsNumericString
          thousandSeparator
          allowNegative={false}
          isAllowed={({ floatValue, formattedValue }) => {
            if (floatValue === undefined) return true;
            if (floatValue < 0) return false;
            if (formattedValue === '-') return false;
            return true;
          }}
          onBlur={onSettlementAmountBlur}
          getInputRef={inputRef}
          autoFocus
          placeholder={isAVMode ? "Enter AV value" : "Enter settlement value"}
          fullWidth
          variant="outlined"
          disabled={isWithdrawal}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <span style={{ color: isAVMode ? '#ff9800' : 'inherit', fontWeight: 'bold' }}>
                  {isAVMode ? 'AV' : '$'}
                </span>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isAVMode && roundingInfo.direction && (
                  <InputAdornment position="end" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: roundingInfo.borderColor,
                        fontSize: '18px',
                        fontWeight: 'bold',
                        lineHeight: 1
                      }}
                    >
                      {roundingInfo.direction === 'up' ? '↑' : roundingInfo.direction === 'down' ? '↓' : ''}
                    </Typography>
                  </InputAdornment>
                )}
                <Tooltip title={isAVMode ? "Switch to Market Value" : "Switch to AV"}>
                  <IconButton
                    size="small"
                    onClick={handleModeToggle}
                    sx={{ padding: '4px' }}
                  >
                    <AutorenewIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            inputProps: { autoComplete: 'off' },
          }}
          onKeyDown={handleKeyPress}
          sx={{
            '& .MuiInputBase-root': {
              border: isAVMode ? "1px solid #ff9800" : `1px solid ${roundingInfo.borderColor}`,
            },
            '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
          }}
        />
        
        {/* Display converted value and withdrawal checkbox in a row */}
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary', 
              ml: 2,
              visibility: settlementRaw && settlementRaw !== '' ? 'visible' : 'hidden'
            }}
          >
                {(() => {
                  if (settlementRaw && settlementRaw !== '') {
                    const n = parseInt(settlementRaw, 10);
                    const pa = getAVUtil(caseObject);
                    let displayText = '';
                    
                    if (isAVMode) {
                      const mv = Math.round(n / conversionFactor);
                      displayText = `MV: $${mv.toLocaleString()}`;
                    } else {
                      const avFinal = applySettlementRounding(n, false, conversionFactor, rounding, pa);
                      displayText = `AV: ${avFinal.toLocaleString()}`;
                    }
                    
                    const roundingArrow = !isAVMode && roundingInfo.direction ? (
                      <span style={{ 
                        color: roundingInfo.borderColor,
                        marginLeft: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {roundingInfo.direction === 'up' ? '↑' : roundingInfo.direction === 'down' ? '↓' : ''}
                      </span>
                    ) : null;
                    
                    const { percent, color, text } = computeReductionPercent(n, isAVMode, pa, ifmv);
                    const reductionText = percent !== null ? <span style={{ color }}>{` (${text})`}</span> : null;
                    
                    return (
                      <>
                        {displayText}
                        {roundingArrow}
                        {reductionText}
                      </>
                    );
                  } else {
                    return 'MV: $0 (0.00%)';
                  }
                })()}
          </Typography>
          
          {/* Withdrawal Checkbox and Reason - moved to the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="checkbox"
              id="withdrawal-checkbox"
              checked={isWithdrawal}
              onChange={(e) => {
                setIsWithdrawal(e.target.checked);
                if (!e.target.checked) {
                  setWithdrawalError(''); // Clear error when withdrawal is unchecked
                  setWithdrawalReason(''); // Reset withdrawal reason when unchecked
                  setCustomWithdrawalReason(''); // Reset custom reason when unchecked
                }
              }}
              style={{ marginRight: '4px' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
              Withdrawal
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Withdrawal Reason - only show when withdrawal is checked */}
      {isWithdrawal && (
        <Box sx={{ mb: 0.7 }}>
          <Typography variant="body2" sx={{ mb: 0, color: 'text.secondary' }}>
            Withdrawal Reason
          </Typography>
          <FormControl fullWidth>
            {withdrawalReason === 'Other' ? (
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Enter custom withdrawal reason"
                value={customWithdrawalReason}
                onChange={(e) => {
                  setCustomWithdrawalReason(e.target.value);
                  setWithdrawalError(''); // Clear error when user types
                }}
              />
            ) : (
              <Select
                value={withdrawalReason}
                onChange={(e) => {
                  setWithdrawalReason(e.target.value);
                  setWithdrawalError(''); // Clear error when user selects a reason
                }}
                variant="outlined"
                size="small"
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select withdrawal reason
                </MenuItem>
                {withdrawalReasons.map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
          {withdrawalError && (
            <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
              {withdrawalError}
            </Typography>
          )}
        </Box>
      )}

      {!isWithdrawal && (
        <Box sx={{ mb: 1, display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              Rounding
            </Typography>
            <FormControl fullWidth>
              <Select
                value={rounding}
                onChange={(e) => setRounding(e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value={0}>Exact</MenuItem>
                <MenuItem value={10}>Nearest 10</MenuItem>
                <MenuItem value={50}>Nearest 50</MenuItem>
                <MenuItem value={100}>Nearest 100</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              Stage
            </Typography>
            <FormControl
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: stage === 'BAR' ? '#ff9800' : undefined,
                },
                '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: stage === 'BAR' ? '#ff9800' : undefined,
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: stage === 'BAR' ? '#ff9800' : undefined,
                },
              }}
            >
              <Select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value={'SCAR'} disabled={!scarStageAvailable}>SCAR</MenuItem>
                <MenuItem value={'Art7'} disabled={!art7StageAvailable}>Art 7</MenuItem>
                <MenuItem value={'BAR'} disabled={art7StageAvailable}>BAR</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      {/* Download Stips Checkbox */}
      {isWithdrawal ? (
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <FormControl sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', marginTop: '25px', }}>
                <input
                  type="checkbox"
                  id="download-stips-checkbox"
                  checked={downloadStips}
                  onChange={(e) => setDownloadStips(e.target.checked)}
                  style={{ marginRight: '8px', flexShrink: 0 }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  Download Stips
                </Typography>
              </Box>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 0, color: 'text.secondary' }}>
              Stage
            </Typography>
            <FormControl
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: stage === 'BAR' ? '#ff9800' : undefined,
                },
                '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: stage === 'BAR' ? '#ff9800' : undefined,
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: stage === 'BAR' ? '#ff9800' : undefined,
                },
              }}
            >
              <Select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value={'SCAR'} disabled={!scarStageAvailable}>SCAR</MenuItem>
                <MenuItem value={'Art7'} disabled={!art7StageAvailable}>Art 7</MenuItem>
                <MenuItem value={'BAR'} disabled={art7StageAvailable}>BAR</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                id="download-stips-checkbox"
                checked={downloadStips}
                onChange={(e) => setDownloadStips(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Download Stips
              </Typography>
            </Box>
          </FormControl>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSettlement}
          disabled={!(settlementRaw ?? '').trim() || isSubmitting}
          sx={{ minWidth: 100 }}
        >
          {isSubmitting ? 'Settling...' : 'Settle'}
        </Button>
      </Box>
    </Box>
  );
};

const SettlementButton = ({ 
  caseObject, 
  onSettlementClick, 
  onSettlementComplete,
  disabledReason = '',
  buttonClassName = 'p-1',
  buttonSx,
  iconProps,
  /** When provided, the parent controls the popover and the gavel trigger is not rendered */
  anchorEl,
  onClose,
}) => {
  const [internalAnchorEl, setInternalAnchorEl] = useState(null);
  const isControlled = anchorEl !== undefined;
  const settlementAnchorEl = isControlled ? anchorEl : internalAnchorEl;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUser();

  // Get the most recent Aventine offer from negotiations
  const mostRecentAventineOffer = getMostRecentOfferValue(caseObject?.Negotiations, 'Aventine');
  const getCompSheet = usePersistedDataStore((state) => state.compSheet);
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  
  // Calculate ProprietyWorkup on the fly since it's not in caseObject
  // Find the compSheet entry for this case
  const caseCompSheet = useMemo(() => {
    if (!Array.isArray(getCompSheet) || !caseObject?.parcel_id) return null;
    // Find the compSheet entry that matches this case's parcel_id
    return getCompSheet.find(sheet => {
      if (Array.isArray(sheet.parcel_id)) {
        return sheet.parcel_id.includes(caseObject.parcel_id);
      }
      return sheet.parcel_id === caseObject.parcel_id;
    });
  }, [getCompSheet, caseObject?.parcel_id]);
  
  // Calculate the workup value
  const calculatedWorkupValue = useMemo(() => {
    if (!caseCompSheet || !caseObject) {
      return null;
    }
    const workupResult = calculateProprietyWorkup(caseObject, caseCompSheet);
    // Return the workup value if it's not 'loading', otherwise return null
    return workupResult === 'loading' ? null : workupResult;
  }, [caseObject, caseCompSheet, getNegotiationObj?.reportType]);
  
  // If no offer exists, default to Workup Value (ProprietyWorkup) if available
  // Track whether the value is in MV or AV terms
  let initialOfferValue = null;
  let isInitialValueInMV = false;
  
  if (mostRecentAventineOffer !== null) {
    // Most recent offer from negotiations is in AV terms
    initialOfferValue = mostRecentAventineOffer;
    isInitialValueInMV = false;
  } else if (caseObject?.OfferValue != null && caseObject.OfferValue !== '') {
    // OfferValue is in AV terms, convert to MV
    initialOfferValue = convertAVToMV(caseObject?.OfferValue, caseObject.EQ, caseObject.RAR);
    isInitialValueInMV = true;
  } else {
    // Try to use calculated workup value or fall back to caseObject.ProprietyWorkup if available
    const workupValue = calculatedWorkupValue ?? caseObject?.ProprietyWorkup;
    if (workupValue != null && workupValue !== undefined && workupValue > 0) {
      // Workup Value is already in MV terms
      initialOfferValue = workupValue;
      isInitialValueInMV = true;
    }
  }

  const handleSettlementClose = useCallback(() => {
    setInternalAnchorEl(null);
    onClose?.();
  }, [onClose]);

  const handleSettlementSubmit = useCallback(async (settlementData) => {
    setIsSubmitting(true);    
    try {
      const context = createOfferContext(user);
      
      // Process settlement with the provided data and context - same as MultiSettlementDialog
      await processSettlement({
        ...caseObject,
        settlementValue: settlementData.settlementValue,
        settlementDetails: settlementData
      }, context);
      
      // Show success toast
      toast.success('Case settled successfully');
      
      // Call the parent handler if provided
      if (onSettlementClick) {
        onSettlementClick(caseObject);
      }
      
      // Call the settlement complete handler if provided
      if (onSettlementComplete) {
        onSettlementComplete(caseObject);
      }
    } catch (error) {
      logger.error('Error processing settlement:', error);
      toast.error(error.message || 'Error processing settlement', { autoClose: false });
    } finally {
      setIsSubmitting(false);
    }
  }, [caseObject, onSettlementClick, onSettlementComplete, user, getNegotiationObj]);

  const settlementOpen = Boolean(settlementAnchorEl);
  const settlementId = settlementOpen ? 'settlement-popover' : undefined;

  return (
    <>
      {!isControlled && (
        <SvgIconButton
          icon={GavelIcon}
          onClick={(event) => setInternalAnchorEl(event.currentTarget)}
          tooltip={disabledReason || "Settle Case"}
          className={buttonClassName}
          sx={buttonSx}
          iconProps={iconProps}
          disabled={Boolean(disabledReason)}
          aria-describedby={settlementId}
        />
      )}
      
      <Popover
        id={settlementId}
        open={settlementOpen}
        disableAutoFocus
        disableEnforceFocus
        anchorEl={settlementAnchorEl}
        onClose={handleSettlementClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <SettlementPopup
          handleClose={handleSettlementClose}
          onSettlementClick={handleSettlementSubmit}
          caseObject={caseObject}
          currentOfferValue={initialOfferValue}
          isInitialValueInMV={isInitialValueInMV}
          ifmv={getIFMVUtil(caseObject)}
          isSubmitting={isSubmitting}
        />
      </Popover>
    </>
  );
};

export default React.memo(SettlementButton);
